import React from 'react'
import type { CachedDbMeta } from './db-cache.js'
import { formatFileSize } from './db-cache.js'
import { formatMeasureDistance } from './measure-tool.js'

// ── Cached DB Row (shared between sidebar dropdown and welcome overlay) ──

function CachedDbRow({
  db,
  isActive,
  compact,
  onSwitch,
  onDelete,
}: {
  db: CachedDbMeta
  isActive?: boolean
  compact?: boolean
  onSwitch: (db: CachedDbMeta) => void
  onDelete: (e: React.MouseEvent, id: string) => void
}) {
  const dateStr = new Date(db.uploadedAt).toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  if (compact) {
    return (
      <div
        className={`group flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-500/10 ${isActive ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-100' : 'text-gray-700 dark:text-slate-200'}`}
        onClick={() => onSwitch(db)}
      >
        <div className="min-w-0 flex-1">
          <div className="truncate">{db.fileName}</div>
          <div className="text-[10px] text-gray-400 dark:text-slate-500">
            {formatFileSize(db.fileSize)} · {dateStr}
          </div>
        </div>
        <button
          className="rounded p-0.5 text-gray-300 opacity-0 hover:text-red-500 group-hover:opacity-100 dark:text-slate-500 dark:hover:text-red-300"
          onClick={(e) => onDelete(e, db.id)}
          title="Remove"
        >
          ✕
        </button>
      </div>
    )
  }
  return (
    <div
      className="group flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2.5 last:border-b-0 hover:bg-blue-50 dark:border-slate-700 dark:hover:bg-blue-500/10"
      onClick={() => onSwitch(db)}
    >
      <span className="text-lg">📄</span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-gray-800 dark:text-slate-100">{db.fileName}</div>
        <div className="text-xs text-gray-400 dark:text-slate-500">
          {formatFileSize(db.fileSize)} · {dateStr}
        </div>
      </div>
      <button
        className="rounded p-1 text-gray-300 opacity-0 transition hover:bg-red-50 hover:text-red-500 group-hover:opacity-100 dark:text-slate-500 dark:hover:bg-red-500/10 dark:hover:text-red-300"
        onClick={(e) => onDelete(e, db.id)}
        title="Remove from cache"
      >
        ✕
      </button>
    </div>
  )
}

// ── DB Selector Dropdown ──

type DbSelectorProps = {
  dbName: string
  activeDbId: string | null
  showDropdown: boolean
  setShowDropdown: (v: boolean | ((prev: boolean) => boolean)) => void
  cachedDbs: CachedDbMeta[]
  onSwitch: (db: CachedDbMeta) => void
  onDelete: (e: React.MouseEvent, id: string) => void
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export function DbSelector({
  dbName,
  activeDbId,
  showDropdown,
  setShowDropdown,
  cachedDbs,
  onSwitch,
  onDelete,
  onFileChange,
}: DbSelectorProps) {
  return (
    <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-slate-700">
      <div className="relative min-w-0 flex-1">
        <button
          className="flex w-full items-center gap-1 truncate rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          title={dbName}
          onClick={() => setShowDropdown((v: boolean) => !v)}
        >
          <span className="truncate">{dbName}</span>
          <span className="ml-auto text-[10px]">▾</span>
        </button>
        {showDropdown && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setShowDropdown(false)} />
            <div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:shadow-black/40">
              {cachedDbs.map((db) => (
                <CachedDbRow
                  key={db.id}
                  db={db}
                  isActive={db.id === activeDbId}
                  compact
                  onSwitch={onSwitch}
                  onDelete={onDelete}
                />
              ))}
              {cachedDbs.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-400 dark:text-slate-500">No cached databases</div>
              )}
            </div>
          </>
        )}
      </div>
      <label className="cursor-pointer whitespace-nowrap rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600">
        Load DB
        <input type="file" accept=".sqlite,.db,.sqlite3,.csv" className="hidden" onChange={onFileChange} />
      </label>
    </div>
  )
}

// ── Welcome Overlay ──

type WelcomeOverlayProps = {
  cachedDbs: CachedDbMeta[]
  onSwitch: (db: CachedDbMeta) => void
  onDelete: (e: React.MouseEvent, id: string) => void
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export function WelcomeOverlay({ cachedDbs, onSwitch, onDelete, onFileChange }: WelcomeOverlayProps) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-50 px-4 dark:bg-slate-950">
      <h2 className="mb-2 text-2xl font-bold text-gray-800 dark:text-slate-100">TrackPoint Debug Viewer</h2>
      <p className="mb-6 text-center text-gray-500 dark:text-slate-400">
        Drag & drop your appData.sqlite or 一生足迹 CSV file to visualize GPS track points
      </p>
      <label className="border-3 flex h-48 w-80 cursor-pointer flex-col items-center justify-center rounded-2xl border-dashed border-gray-300 transition hover:border-blue-500 hover:bg-blue-50/50 dark:border-slate-600 dark:bg-slate-900/70 dark:hover:border-blue-400 dark:hover:bg-blue-500/10 sm:w-96">
        <span className="mb-3 text-5xl">📍</span>
        <span className="text-gray-600 dark:text-slate-300">Drop .sqlite or .csv file here</span>
        <input type="file" accept=".sqlite,.db,.sqlite3,.csv" className="hidden" onChange={onFileChange} />
      </label>
      {cachedDbs.length > 0 && (
        <div className="mt-6 w-80 sm:w-96">
          <h3 className="mb-2 text-sm font-semibold text-gray-500 dark:text-slate-400">Recent Databases</h3>
          <div className="rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            {cachedDbs.map((db) => (
              <CachedDbRow key={db.id} db={db} onSwitch={onSwitch} onDelete={onDelete} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Map Legend ──

export function MapLegend() {
  return (
    <div className="absolute right-3 top-14 z-10 hidden rounded-xl bg-white/90 px-3 py-2 text-[10px] text-gray-500 shadow-lg backdrop-blur-sm dark:bg-slate-900/90 dark:text-slate-300 dark:shadow-black/40 sm:block">
      <div className="mb-1 font-semibold">Accuracy</div>
      <div className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full bg-[#34C759]" /> &lt;10m
      </div>
      <div className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full bg-[#007AFF]" /> 10–30m
      </div>
      <div className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full bg-[#FF9500]" /> 30–65m
      </div>
      <div className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full bg-[#FF3B30]" /> &gt;65m
      </div>
      <div className="mb-1 mt-2 font-semibold">Line</div>
      <div className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full bg-[#007AFF]" /> &lt;2min
      </div>
      <div className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full bg-[#FF9500]" /> 2–10min
      </div>
      <div className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full bg-[#FF3B30]" /> &gt;10min
      </div>
    </div>
  )
}

export function MeasureToolbar({
  active,
  totalMeters,
  pointCount,
  onToggle,
  onClear,
}: {
  active: boolean
  totalMeters: number
  pointCount: number
  onToggle: () => void
  onClear: () => void
}) {
  const hasPoints = pointCount > 0
  return (
    <div className="absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-xl bg-white/90 px-2 py-1.5 text-xs text-slate-700 shadow-lg backdrop-blur-sm dark:bg-slate-900/90 dark:text-slate-200 dark:shadow-black/40">
      <button
        onClick={onToggle}
        title={active ? 'Exit measure mode (Esc)' : 'Click two or more points on the map to measure distance'}
        className={`rounded px-2 py-0.5 font-medium transition ${
          active
            ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600'
        }`}
      >
        📏 {active ? 'Measuring…' : 'Measure'}
      </button>
      {hasPoints && (
        <>
          <span className="font-mono font-semibold text-blue-700 dark:text-blue-300">
            {formatMeasureDistance(totalMeters)}
          </span>
          <span className="text-gray-400 dark:text-slate-500">({pointCount} pts)</span>
          <button
            onClick={onClear}
            title="Clear measurement"
            className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
          >
            ✕
          </button>
        </>
      )}
    </div>
  )
}
